import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  Box,
  Button,
  Container,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import { LoadingSpinner } from '../components/LoadingSpinner';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, signInWithGoogle, configMissing } = useAuth();
  const navigate = useNavigate();
  const { mode, toggleTheme } = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      // Authentication succeeded. Routing/Role protection is handled in App.tsx or Dashboard.
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      setLoading(true);
      await signInWithGoogle();
      // Note: checkOwnerStatus will happen on redirect back from Google in AuthContext
    } catch (err: any) {
      setError(err.message || 'Failed to sign in with Google.');
      console.error('Google Auth error:', err);
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: mode === 'dark'
          ? 'linear-gradient(135deg, #111827 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #f0f4ff 0%, #e6efff 100%)',
        backgroundSize: 'cover',
        p: 2,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <Tooltip title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}>
            <IconButton 
              onClick={toggleTheme} 
              size="large"
              sx={{
                bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.04)',
                '&:hover': {
                  bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)',
                }
              }}
            >
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Tooltip>
        </Box>
        
        <Card 
          elevation={24}
          className="scale-in"
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: mode === 'dark'
              ? '0 10px 40px rgba(0,0,0,0.5)'
              : '0 10px 40px rgba(0,0,0,0.1)',
          }}
        >
          <Box
            sx={{
              p: 3,
              background: mode === 'dark'
                ? 'linear-gradient(135deg, #303f9f 0%, #512da8 100%)'
                : 'linear-gradient(135deg, #3f51b5 0%, #673ab7 100%)',
              color: '#fff',
              textAlign: 'center',
            }}
          >
            <Typography 
              component="h1" 
              variant="h4" 
              fontWeight="700"
              sx={{
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                letterSpacing: 0.5,
              }}
            >
              Dhishank Dashboard
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 1, opacity: 0.9 }}>
              Sign in to access your portal
            </Typography>
          </Box>
          
          <CardContent sx={{ p: 4 }}>
            {configMissing && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <strong>Missing Supabase configuration.</strong> Create{' '}
                <code>crm-app/.env</code> with{' '}
                <code>REACT_APP_SUPABASE_URL</code> and{' '}
                <code>REACT_APP_SUPABASE_ANON_KEY</code>, then restart the dev server.
              </Alert>
            )}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  animation: 'shake 0.5s',
                  '@keyframes shake': {
                    '0%, 100%': { transform: 'translateX(0)' },
                    '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-5px)' },
                    '20%, 40%, 60%, 80%': { transform: 'translateX(5px)' },
                  }
                }}
              >
                {error}
              </Alert>
            )}
            
            <Box 
              component="form" 
              onSubmit={handleSubmit} 
              sx={{ mt: error ? 0 : 1 }}
            >
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleGoogleLogin}
                disabled={loading}
                sx={{ 
                  mb: 3,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderColor: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                  color: mode === 'dark' ? '#fff' : 'inherit',
                  '&:hover': {
                    borderColor: mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
                    backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  }
                }}
                startIcon={
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                }
              >
                Continue with Google (Owner)
              </Button>

              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  OR HANDLER LOGIN
                </Typography>
              </Divider>

              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ 
                  mt: 4, 
                  mb: 2,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  position: 'relative',
                  overflow: 'hidden',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '200%',
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    transform: 'skewX(-20deg)',
                    transition: 'all 0.6s ease',
                  },
                  '&:hover:after': {
                    left: '100%',
                  }
                }}
              >
                {loading ? 'Processing...' : 'Sign In with Email'}
              </Button>
              
              {loading && <LoadingSpinner size={30} message="Processing request..." />}
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic', mb: 2 }}>
                This secure dashboard is restricted to Owners, Admins, and Handlers. Unauthorized access attempts are logged.
              </Typography>
              
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                Made by <a href="https://genrecai.com" target="_blank" rel="noopener noreferrer" style={{ color: mode === 'dark' ? '#90caf9' : '#1976d2', textDecoration: 'none', fontWeight: 'bold' }}>GenrecAI</a>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}